/* eslint-disable complexity */
import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { MessagingBlock } from '@strata/react-messaging-block';
import { Icon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { SuccessToast } from '@strata/react-toast-message';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import { SendOutboundFileDialog } from './dialogs/SendOutboundFileDialog';
import { OrtfOutboundFileScriptsLayout } from './OrtfOutboundFileScriptsLayout';
import { RemoveOutboundFileScriptDialog } from './RemoveOutboundFileScriptDialog';

import { handleError } from 'services/Error';
import {
    useDownloadOrtfFileMutation,
    useGetOutboundFileScriptsQuery,
    useGetSentOutboundFileScriptsQuery,
    useSendOutboundFilesV2Mutation,
} from 'store/api/ndpApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

const ORTF_FILE_SCRIPT_SORT_DEFAULTS = [{ id: 'patientName', desc: false }];

const DEFAULT_BACK_LABEL = 'Outbound Files';
const DEFAULT_TITLE = 'Outbound File Details';

const defaultOutboundFileStatus = {
    indicatorStatus: StatusIndicator.Status.DEFAULT,
    message: 'Needs Review',
};

const outboundFilePropType = PropTypes.shape({
    fileName: PropTypes.string,
    id: PropTypes.string,
    npi: PropTypes.string,
    outboundFileId: PropTypes.string,
    payer: PropTypes.string,
    pharmacy: PropTypes.string,
    pharmacistInCharge: PropTypes.string,
    pharmacyAddress: PropTypes.string,
    pharmacyEmail: PropTypes.string,
    prescriptions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});

const deriveOutboundFilePayers = (payer, scripts = []) => {
    if (payer) {
        return payer;
    }

    const uniquePayers = Array.from(
        new Set(scripts.map(({ healthPlan }) => healthPlan).filter(Boolean)),
    ).sort(sortAsc);
    return uniquePayers.join(', ');
};

const countUniqueValues = (rows, key) =>
    new Set(rows.map((row) => row[key]).filter((value) => value != null && value !== '')).size;

const buildOrtfFileScriptFilterCategories = (scripts) => {
    const categories = [
        { key: 'healthPlan', title: 'Payer', showCounts: true },
        { key: 'drugName', title: 'Product', showCounts: true },
    ];

    if (scripts.some(({ source }) => source != null && source !== '')) {
        categories.push({ key: 'source', title: 'Source', showCounts: true });
    }

    return categories;
};

const ortfFileScriptKpiData = [
    {
        label: 'Prescriptions',
        value: (gridData) => gridData?.length ?? 0,
    },
    {
        label: 'Products',
        value: (gridData) => countUniqueValues(gridData ?? [], 'drugName'),
    },
    {
        label: 'Payers',
        value: (gridData) => countUniqueValues(gridData ?? [], 'healthPlan'),
    },
    {
        label: 'Sources',
        value: (gridData) => countUniqueValues(gridData ?? [], 'source'),
    },
];

const buildOrtfFileScriptColumns = ({ onRemoveScript, readOnly, scripts }) => [
    { accessorKey: 'patientName', header: 'Patient Name', size: 180 },
    { accessorKey: 'drugName', header: 'Product', size: 200 },
    { accessorKey: 'healthPlan', header: 'Payer', size: 180 },
    { accessorKey: 'quantity', header: 'Qty', size: 100 },
    { accessorKey: 'daysSupply', header: 'Days Supply', size: 120 },
    { accessorKey: 'instructions', header: 'Directions', size: 220 },
    ...(scripts.some(({ source }) => source != null && source !== '')
        ? [{ accessorKey: 'source', header: 'Source', size: 160 }]
        : []),
    ...(readOnly
        ? []
        : [
              {
                  accessorKey: 'rxNumber',
                  cell: ({ row }) => (
                      <Icon
                          icon={IconNames.CROSS}
                          intent={Icon.Intent.DEFAULT}
                          onClick={() => onRemoveScript(row.original)}
                          title="Remove from outbound file"
                          tooltipContent="Remove from outbound file"
                      />
                  ),
                  enableSorting: false,
                  header: '',
                  size: 48,
              },
          ]),
];

const useOrtfFileScriptsQuery = ({ outboundFileId, readOnly }) => {
    const plannedScriptsQuery = useGetOutboundFileScriptsQuery(
        { outboundFileId },
        { skip: !outboundFileId || readOnly },
    );
    const sentScriptsQuery = useGetSentOutboundFileScriptsQuery(
        { outboundFileId },
        { skip: !outboundFileId || !readOnly },
    );
    return readOnly ? sentScriptsQuery : plannedScriptsQuery;
};

export const OrtfOutboundFileDetailsTab = ({
    backLabel = DEFAULT_BACK_LABEL,
    createdDate: createdDateOverride = undefined,
    fileDate: fileDateOverride = undefined,
    onBack,
    onFileSent = undefined,
    outboundFile,
    outboundFileStatus: outboundFileStatusOverride = undefined,
    readOnly = false,
    title = DEFAULT_TITLE,
}) => {
    const [removedRxNumbers, setRemovedRxNumbers] = useState(() => new Set());
    const [scriptToRemove, setScriptToRemove] = useState(null);
    const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
    const outboundFileId = outboundFile?.outboundFileId ?? outboundFile?.id;
    const [downloadOrtfFile, { isLoading: isDownloading }] = useDownloadOrtfFileMutation();
    const [sendOutboundFiles, { isLoading: isSending }] = useSendOutboundFilesV2Mutation();

    const handleSendFile = async () => {
        if (!outboundFileId) {
            return;
        }

        try {
            await sendOutboundFiles({ outboundFileIds: [outboundFileId] }).unwrap();
            SuccessToast({ message: 'Outbound file sent successfully.' });
            setIsSendDialogOpen(false);
            onFileSent?.(outboundFileId);
            onBack();
        } catch (err) {
            handleError('Unable to send outbound file. Please try again.', err);
        }
    };

    const handleDownloadFile = async () => {
        const fileName = outboundFile?.fileName;
        if (!outboundFileId || !fileName) {
            return;
        }

        try {
            const blob = await downloadOrtfFile({ outboundFileId }).unwrap();
            const url = URL.createObjectURL(
                new File([blob], fileName, { type: blob.type || 'text/csv' }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            handleError('Unable to download file. Please try again.', err);
        }
    };

    const {
        activeFilters,
        filterPaneWidth,
        sort,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSort,
    } = useGridState(Grids.ORTF_OUTBOUND_FILE_SCRIPTS, ORTF_FILE_SCRIPT_SORT_DEFAULTS);

    const { data, isFetching, isError } = useOrtfFileScriptsQuery({ outboundFileId, readOnly });

    const scripts = useMemo(
        () => (data ?? []).filter((script) => !removedRxNumbers.has(script.rxNumber)),
        [data, removedRxNumbers],
    );

    const filterCategories = useMemo(() => buildOrtfFileScriptFilterCategories(scripts), [scripts]);

    const scriptColumns = useMemo(
        () =>
            buildOrtfFileScriptColumns({
                onRemoveScript: setScriptToRemove,
                readOnly,
                scripts,
            }),
        [readOnly, scripts],
    );

    const today = dayjs();
    const createdDate = createdDateOverride ?? formatDate(today, DateFormats.DATE_TIME);
    const fileDate = fileDateOverride ?? formatDate(today, DateFormats.DATE);
    const outboundFileStatus = outboundFileStatusOverride ?? defaultOutboundFileStatus;
    const payers = useMemo(
        () => deriveOutboundFilePayers(outboundFile?.payer, scripts),
        [outboundFile?.payer, scripts],
    );

    if (!outboundFileId || isError) {
        return (
            <div className="padding__around--large">
                <ActionButtonPageTitle label={backLabel} onClick={onBack} title={title} />
                <MessagingBlock
                    className="margin__top--medium"
                    secondaryText={
                        !outboundFileId
                            ? 'Outbound file id is required to load scripts.'
                            : 'Unable to load outbound file scripts.'
                    }
                    size={MessagingBlock.Sizes.MEDIUM}
                />
            </div>
        );
    }

    return (
        <>
            <FullscreenLoading isShowing={isSending} loadingText="Sending outbound file..." />
            <OrtfOutboundFileScriptsLayout
                activeFilters={activeFilters}
                backLabel={backLabel}
                createdDate={createdDate}
                fileDate={fileDate}
                payers={payers}
                filterCategories={filterCategories}
                filterPaneWidth={filterPaneWidth}
                goBack={onBack}
                isDownloading={isDownloading}
                isFetching={isFetching}
                isSending={isSending}
                kpiData={ortfFileScriptKpiData}
                onDownloadFile={handleDownloadFile}
                onSendFile={() => setIsSendDialogOpen(true)}
                outboundFile={outboundFile}
                outboundFileStatus={outboundFileStatus}
                scriptColumns={scriptColumns}
                scripts={scripts}
                showSendFile={!readOnly}
                sort={sort}
                title={title}
                updateFilterPaneWidth={updateFilterPaneWidth}
                updateGridActiveFilters={updateGridActiveFilters}
                updateGridSort={updateGridSort}
            />
            {readOnly ? null : (
                <>
                    <RemoveOutboundFileScriptDialog
                        isOpen={!!scriptToRemove}
                        onClose={() => setScriptToRemove(null)}
                        onRemoved={(removedRxNumber) => {
                            setRemovedRxNumbers((current) => new Set(current).add(removedRxNumber));
                        }}
                        outboundFileId={outboundFileId}
                        rxNumber={scriptToRemove?.rxNumber}
                    />
                    <SendOutboundFileDialog
                        isOpen={isSendDialogOpen}
                        isSending={isSending}
                        pharmacy={outboundFile?.pharmacy}
                        pharmacistInCharge={outboundFile?.pharmacistInCharge}
                        pharmacyAddress={outboundFile?.pharmacyAddress}
                        pharmacyEmail={outboundFile?.pharmacyEmail}
                        healthPlans={payers}
                        prescriptions={scripts.length}
                        onClose={() => setIsSendDialogOpen(false)}
                        onConfirm={handleSendFile}
                    />
                </>
            )}
        </>
    );
};

OrtfOutboundFileDetailsTab.propTypes = {
    backLabel: PropTypes.string,
    createdDate: PropTypes.string,
    fileDate: PropTypes.string,
    onBack: PropTypes.func.isRequired,
    onFileSent: PropTypes.func,
    outboundFile: outboundFilePropType.isRequired,
    outboundFileStatus: PropTypes.shape({
        indicatorStatus: PropTypes.string,
        message: PropTypes.string.isRequired,
    }),
    readOnly: PropTypes.bool,
    title: PropTypes.string,
};
